import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Paperclip, DownloadSimple, X, FileText, Image as ImageIcon, File as FileIcon, Sparkle, Eye } from "@phosphor-icons/react";
import AttachmentPreviewDialog from "@/components/AttachmentPreviewDialog";

/**
 * NoteAttachments — inline attachment strip for a note.
 *
 * Uploads round-trip via `/notes/{id}/attachments` (multipart) and downloads
 * use a cookie-authed GET. `dropHandleRef` lets a parent-level drop zone
 * reuse the same upload pipeline.
 *
 * Image attachments (`image/*`) render a small inline thumbnail — the
 * download URL is same-origin, so the browser sends the JWT cookie
 * automatically on `<img src>` requests. Clicking a thumbnail opens a
 * lightbox so partners can see the full screenshot in context.
 */

function iconFor(ct = "", filename = "") {
  const type = ct.toLowerCase();
  const ext = (filename.split(".").pop() || "").toLowerCase();
  if (type.startsWith("image/")) return ImageIcon;
  if (type === "application/pdf" || ext === "pdf") return FileText;
  if (["doc", "docx", "txt", "md", "csv"].includes(ext)) return FileText;
  return FileIcon;
}

const isImage = (a) =>
  (a?.content_type || "").toLowerCase().startsWith("image/") ||
  /\.(png|jpe?g|gif|webp)$/i.test(a?.filename || "");

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NoteAttachments({ note, onChange, readOnly = false, dropHandleRef }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, filename } | null
  // Iter 85.57 · Shared universal preview for non-image files (PDF/text/etc).
  const [preview, setPreview] = useState(null); // { url, filename, contentType, size, uploadedBy } | null
  const inputRef = useRef(null);
  const attachments = note?.attachments || [];

  const pick = () => inputRef.current?.click();

  const uploadFile = async (file) => {
    if (!file || !note) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/notes/${note.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.({ ...note, attachments: [...attachments, data] });
      toast.success(`Attached ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (dropHandleRef) dropHandleRef.current = uploadFile;

  const onInputChange = async (ev) => {
    const file = ev.target.files?.[0];
    await uploadFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.filename}"?`)) return;
    try {
      await api.delete(`/notes/${note.id}/attachments/${a.id}`);
      onChange?.({ ...note, attachments: attachments.filter((x) => x.id !== a.id) });
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  if (!note) return null;

  const downloadUrl = (a) => `${API}/notes/${note.id}/attachments/${a.id}/download`;

  // Split images from other files so the image grid can lead the strip —
  // partners parse a screenshot much faster than a filename.
  const imageAtts = attachments.filter(isImage);
  const fileAtts = attachments.filter((a) => !isImage(a));

  return (
    <section data-testid="note-attachments">
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
        <Paperclip size={11} weight="bold" /> Source materials · {attachments.length}
      </div>

      {attachments.length === 0 ? (
        <div className="text-xs text-muted-foreground italic mb-2" data-testid="note-attachments-empty">
          No files attached. Drop a file anywhere on this dialog, or use Attach file below.
        </div>
      ) : (
        <>
          {imageAtts.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3" data-testid="note-attachment-images">
              {imageAtts.map((a) => (
                <div key={a.id} data-testid={`note-attachment-${a.id}`} className="relative group border border-border bg-secondary/20 aspect-video overflow-hidden">
                  <button
                    onClick={() => setLightbox({ url: downloadUrl(a), filename: a.filename })}
                    data-testid={`note-attachment-thumb-${a.id}`}
                    className="w-full h-full block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={`Preview ${a.filename}`}
                  >
                    <img
                      src={downloadUrl(a)}
                      alt={a.filename}
                      loading="lazy"
                      className="w-full h-full object-cover transition-opacity group-hover:opacity-90"
                    />
                  </button>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/80 to-transparent px-2 py-1.5 flex items-center justify-between gap-2 pointer-events-none">
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-foreground truncate flex-1">{a.filename}</div>
                    {a.auto_generated && <Sparkle size={10} weight="fill" className="text-primary shrink-0" title="Auto-generated by YadidAi" />}
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={downloadUrl(a)}
                      data-testid={`note-attachment-download-${a.id}`}
                      className="w-6 h-6 flex items-center justify-center bg-background/90 border border-border text-primary"
                      aria-label={`Download ${a.filename}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DownloadSimple size={11} weight="bold" />
                    </a>
                    {!readOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(a); }}
                        data-testid={`note-attachment-remove-${a.id}`}
                        className="w-6 h-6 flex items-center justify-center bg-background/90 border border-border text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${a.filename}`}
                      >
                        <X size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {fileAtts.length > 0 && (
            <ul className="space-y-1.5 mb-2">
              {fileAtts.map((a) => {
                const Icon = iconFor(a.content_type, a.filename);
                const openPreview = () => setPreview({
                  url: downloadUrl(a),
                  filename: a.filename,
                  contentType: a.content_type,
                  size: a.size,
                  uploadedBy: a.uploaded_by_name,
                });
                return (
                  <li
                    key={a.id}
                    data-testid={`note-attachment-${a.id}`}
                    className="flex items-center gap-3 px-3 py-2 border border-border hover:border-primary/60 hover:bg-secondary/40 transition-colors"
                  >
                    <Icon size={14} weight="bold" className="text-primary shrink-0" />
                    <button
                      type="button"
                      onClick={openPreview}
                      data-testid={`note-attachment-open-${a.id}`}
                      className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                      aria-label={`Preview ${a.filename}`}
                    >
                      <div className="text-sm font-medium truncate flex items-center gap-1.5 hover:text-primary transition-colors">
                        {a.filename}
                        {a.auto_generated && (
                          <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary inline-flex items-center gap-0.5" title="Auto-generated by YadidAi">
                            <Sparkle size={9} weight="fill" /> AI
                          </span>
                        )}
                      </div>
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                        {fmtBytes(a.size)}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ""}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={openPreview}
                      data-testid={`note-attachment-view-${a.id}`}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                      aria-label={`View ${a.filename}`}
                    >
                      <Eye size={13} weight="bold" /> View
                    </button>
                    <a
                      href={downloadUrl(a)}
                      data-testid={`note-attachment-download-${a.id}`}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                      aria-label={`Download ${a.filename}`}
                    >
                      <DownloadSimple size={13} weight="bold" /> Download
                    </a>
                    {!readOnly && (
                      <button
                        onClick={() => remove(a)}
                        data-testid={`note-attachment-remove-${a.id}`}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                        aria-label={`Remove ${a.filename}`}
                        title="Remove"
                      >
                        <X size={13} weight="bold" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            onChange={onInputChange}
            className="hidden"
            data-testid="note-attachment-input"
          />
          <Button
            onClick={pick}
            disabled={uploading}
            variant="outline"
            size="sm"
            className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
            data-testid="note-attachment-add"
          >
            <Paperclip size={12} weight="bold" className="mr-1.5" />
            {uploading ? "Uploading…" : "Attach file"}
          </Button>
        </>
      )}

      {/*
        Simple full-screen preview — clicking anywhere or pressing Esc closes.
        Kept intentionally lightweight so it doesn't drag in a lightbox lib
        for a one-image-at-a-time preview.
      */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-label={lightbox.filename}
          data-testid="note-attachment-lightbox"
        >
          <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-full object-contain shadow-2xl" />
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-6 right-6 w-9 h-9 flex items-center justify-center border border-border bg-card text-foreground hover:text-primary"
            aria-label="Close preview"
          >
            <X size={16} weight="bold" />
          </button>
          <div className="absolute bottom-6 left-6 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            {lightbox.filename}
          </div>
        </div>
      )}

      {/* Iter 85.57 · Shared universal preview for non-image attachments. */}
      <AttachmentPreviewDialog
        open={!!preview}
        onOpenChange={(o) => { if (!o) setPreview(null); }}
        url={preview?.url}
        filename={preview?.filename}
        contentType={preview?.contentType}
        size={preview?.size}
        uploadedBy={preview?.uploadedBy}
      />
    </section>
  );
}
